import { CrossIcon } from "@/Icons/cross";
import { useTerminalsStateRE } from "@/States/Terminals";
import { useGlobalAlert } from "@/States/globalAlert";
import { jpegType, pngType, webpType } from "@/Utils/fileValidation";
import { trpc } from "@/Utils/trpc";
import { CLIPPED_BUTTON_STYLE } from "@/constants/general";
import { useTrans } from "@/translations";

import { type ChangeEvent, useCallback, useMemo, useState } from "react";

import { twMerge } from "tailwind-merge";

import type { NewsfeedPost } from "../types";
import { LibraryUploadZone } from "./libraryUploadZone";

const allowedFileTypes = [jpegType, pngType, webpType];

type Props = {
  newsfeedPost?: NewsfeedPost | null;
  setEditMode: (value: boolean) => void;
  deleteImage: ({
    terminalId,
    deletePost,
  }: {
    terminalId: string;
    deletePost: boolean;
  }) => void;
  refetchNewsfeedPost: () => Promise<any>;
};

export const MessageForm: React.FC<Props> = ({
  newsfeedPost,
  setEditMode,
  deleteImage,
  refetchNewsfeedPost,
}: Props) => {
  const { t } = useTrans();
  const { currentTerminal } = useTerminalsStateRE();
  const trpcUtils = trpc.useUtils();
  const { setGlobalAlert } = useGlobalAlert();

  const [title, setTitle] = useState<string>(newsfeedPost?.title || "");
  const [uploadedImage, setUploadedImage] = useState<File>();
  const [existingImageUrl, setExistingImageUrl] = useState<string>(
    newsfeedPost?.imageUrl || "",
  );
  const [message, setMessage] = useState<string>(newsfeedPost?.message || "");

  const { mutate: createNewsfeedPost } =
    trpc.config.createNewsfeedPost.useMutation({
      onSuccess: async (createdPost) => {
        if (uploadedImage) {
          const { url } = await trpcUtils.newsfeed.getImageUploadUrl.fetch({
            terminalId: currentTerminal.id,
            messageId: createdPost.id.toString(),
            filename: uploadedImage.name,
            contentType: uploadedImage.type,
          });

          await fetch(url, {
            method: "PUT",
            headers: {
              "Content-Type": uploadedImage.type, // MUST match what was signed
            },
            body: uploadedImage,
          });
        }

        refetchNewsfeedPost().then(() => onCancel());
      },
      onError: (error) => {
        setGlobalAlert({
          type: "error",
          message: "errors:createNewsfeedPostFailed",
        });
        console.error("Error creating newsfeed post:", error);
      },
    });

  const { mutate: updateNewsfeedPost } =
    trpc.config.updateNewsfeedPost.useMutation({
      onSuccess: async (updatedPost) => {
        if (uploadedImage) {
          const { url } = await trpcUtils.newsfeed.getImageUploadUrl.fetch({
            terminalId: currentTerminal.id,
            messageId: updatedPost.id.toString(),
            filename: uploadedImage.name,
            contentType: uploadedImage.type,
          });

          await fetch(url, {
            method: "PUT",
            headers: {
              "Content-Type": uploadedImage.type, // MUST match what was signed
            },
            body: uploadedImage,
          });
        } else if (!existingImageUrl) {
          deleteImage({
            terminalId: currentTerminal.id,
            deletePost: false,
          });
        }

        refetchNewsfeedPost().then(() => onCancel());
      },
      onError: (error) => {
        setGlobalAlert({
          type: "error",
          message: "errors:newsfeedPostUpdateFailed",
        });
        console.error("Error updating newsfeed post:", error);
      },
    });

  const onCancel = useCallback(() => {
    setEditMode(false);
    setTitle("");
    setMessage("");
    setExistingImageUrl("");
    setUploadedImage(undefined);
  }, [setEditMode]);

  const previewImageUrl = useMemo(() => {
    if (uploadedImage) {
      return URL.createObjectURL(uploadedImage);
    }
    return existingImageUrl;
  }, [existingImageUrl, uploadedImage]);

  const onPublish = useCallback(async () => {
    if (newsfeedPost) {
      updateNewsfeedPost({
        newsfeedPostId: newsfeedPost.id.toString(),
        title,
        message,
      });
    } else {
      createNewsfeedPost({
        terminalId: currentTerminal.id,
        title,
        message,
      });
    }
  }, [
    createNewsfeedPost,
    title,
    currentTerminal,
    message,
    newsfeedPost,
    updateNewsfeedPost,
  ]);

  return (
    <div className="flex h-full w-full">
      <div className="w-full">
        <div>
          <input
            value={title}
            className="disabled:bg-grey2 h-[35px] w-full pl-2 text-[14px] font-medium"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setTitle(e.target.value)
            }
            placeholder={t("library:messageTitleInputPlaceholder")}
            maxLength={100}
          />
        </div>
        <div className="mt-3">
          <div>
            <textarea
              value={message}
              className="border-grey2 disabled:bg-grey2 min-h-[200px] w-full border border-1 p-2 text-sm"
              onChange={(e: ChangeEvent<any>) => setMessage(e.target.value)}
              placeholder={t("library:messageContentInputPlaceholder")}
              maxLength={2000}
            />
          </div>
        </div>
        <div className="mt-4 flex w-full flex-row gap-[10px]">
          <button
            type="button"
            className={twMerge(
              "inline-flex cursor-pointer items-center gap-[9px] bg-black px-[22px] py-[9px] text-[13px] font-semibold text-white hover:bg-[#3a3838]",
              CLIPPED_BUTTON_STYLE,
            )}
            onClick={onPublish}
            disabled={!title || !message}
          >
            {t("libraryPage:saveMessageButton")}
          </button>
          <button
            type="button"
            className={twMerge(
              "border-grey1 hover:bg-grey1 bg-grey1 inline-flex cursor-pointer items-center gap-[9px] border border-1 px-[4px] py-[9px] text-[13px] font-medium text-[#1d1c1c]",
              CLIPPED_BUTTON_STYLE,
            )}
            onClick={onCancel}
          >
            {t("libraryPage:cancelEditButton")}
          </button>
        </div>
      </div>
      <div className="ml-5 min-h-[310px]">
        {previewImageUrl ? (
          <div className="group relative min-h-[310px] w-[460px]">
            <div
              className="min-h-[310px] w-[460px]"
              style={{
                backgroundImage: `url(${previewImageUrl})`,
                backgroundSize: "contain",
                backgroundRepeat: "no-repeat",
              }}
            />
            {(!!uploadedImage || newsfeedPost?.imageUrl) && (
              <div
                className="group color-white absolute top-0 flex h-full w-full cursor-pointer items-center justify-center"
                onClick={() => {
                  setExistingImageUrl("");
                  setUploadedImage(undefined);
                }}
              >
                <div className="bg-grey1 absolute top-0 hidden h-full w-full opacity-80 group-hover:block" />
                <div className="absolute top-[calc(50%-10px)] left-[calc(50%-10px)] hidden rotate-90 group-hover:block">
                  <CrossIcon width={20} height={20} />
                </div>
              </div>
            )}
          </div>
        ) : (
          <LibraryUploadZone
            allowedFileTypes={allowedFileTypes}
            onAddFile={setUploadedImage}
            uploadZoneStyle="h-[300px] w-[460px]"
            topTextKey="library:uploadMessageImageTopText"
            bottomTextKey="library:uploadMessageImageBottomText"
          />
        )}
      </div>
    </div>
  );
};
